import { FC, useCallback, useEffect, useState } from "react";
import { CDialog } from "../Dialogs/CDialog";
import { performEnterpriseGPTApiCall } from "../../Hooks/useMiddlewareApiCall";
import { ISurveyAnswer, SurveyResponse } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { useNotification } from "../../Contexts/NotificationContext";
import { useConversations, useSurvey } from "../../store/hooks";
import moment from "moment";
import SurveyView from "./SurveyView";
import { resetSurveyAction } from "../../store/actions/surveyAction";

const SurveyHandler: FC = () => {
    const { chats } = useConversations();
    const { dispatchNotification } = useNotification();
    const [showSurvey, setShowSurvey] = useState<boolean>(false);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState<boolean>(false);
    const { survey, dispatch } = useSurvey();   // get the latest survey from the store, if available
    const [answersDict, setAnswersDict] = useState<{ [key: string]: ISurveyAnswer }>({});

    // Show the survey if there is at least one non-draft chat that started before today.
    useEffect(() => {
        if (survey?.id) {
            setShowSurvey(chats.some(c => c.draft === false && moment(c.startedOn).isBefore(moment().startOf('day'))));
        }
    }, [survey, chats]);

    // Enable the submit button if all mandatory questions have been answered.
    useEffect(() => {
        const isValid = survey?.questions?.every(q => !q.isMandatory || (q.id! in answersDict && !!answersDict[q.id!].value)) ?? false;
        setIsSubmitEnabled(isValid);
    }, [survey, answersDict]);

    // Log survey questions when survey model changes (helpful to verify q.id and isMandatory)
    useEffect(() => {
        if (!survey) {
            // console.log('survey: undefined');
            return;
        }

        // console.log('survey.questions:', (survey.questions ?? []).map(q => ({
        //     id: q.id,
        //     idRaw: q.id ? `'${q.id}'` : q.id,
        //     isMandatory: q.isMandatory,
        //     type: q.type,
        //     text: q.text
        // })));
    }, [survey]);

    // Log answersDict and submit-enabled state when answers change (state updates are async so useEffect is reliable)
    useEffect(() => {
        // console.log('answersDict keys:', Object.keys(answersDict));
        // console.log('answersDict full:', answersDict);
        // console.log('isSubmitEnabled:', isSubmitEnabled);
    }, [answersDict, isSubmitEnabled]);

    // Diagnostics: print per-question validation details so we can see why the overall check fails
    useEffect(() => {
        if (!survey?.questions) return;
        const diagnostics = (survey.questions ?? []).map(q => {
            const key = String(q.id ?? '');
            const answer = answersDict[key];
            const rawVal = answer?.value;
            const strVal = rawVal == null ? rawVal : String(rawVal);
            const trimmed = typeof strVal === 'string' ? strVal.trim() : strVal;
            const existsInDict = key in answersDict;
            // basic trimmed non-empty check; this is only a diagnostic, not changing validation yet
            const passesValueCheck = !(trimmed == null) && (typeof trimmed !== 'string' || trimmed.length > 0) && !(trimmed === '0' && q.type === 'Rating');
            const passes = !q.isMandatory || (existsInDict && passesValueCheck);
            return { id: key, isMandatory: q.isMandatory, type: q.type, rawVal, trimmed, existsInDict, passes };
        });
        // console.log('survey validation diagnostics:', diagnostics);
    }, [survey, answersDict]);

    const onAnswerChange = (answer: ISurveyAnswer) => {
        setAnswersDict(a => ({
            ...a,
            [answer.id!]: answer
        }));
    };

    const onSubmitSurvey = useCallback(() => {
        performEnterpriseGPTApiCall(async (client, cancelToken) => {
            try {
                const surveyResponse: SurveyResponse = new SurveyResponse({
                    surveyId: survey?.id,
                    answers: Object.values(answersDict).sort((a, b) => parseInt(a.id!) - parseInt(b.id!))
                });

                await client.submitSurveyResponse(surveyResponse, cancelToken);

                setShowSurvey(false);
                dispatch(resetSurveyAction());
            } catch (e) {
                dispatchNotification('error.api.post.conversation', 'error');
            }
        });
    }, [survey, answersDict]);

    // display the survey dialog
    return <CDialog open={showSurvey} onPrimaryAction={onSubmitSurvey} primaryActionText="Submit" disablePrimaryAction={!isSubmitEnabled}>
        <SurveyView surveyModel={survey} onAnswerChange={onAnswerChange} />
    </CDialog>
};

export default SurveyHandler;